import { CategoryScale } from "chart.js";
import Chart from "chart.js/auto";
import { RevenueSource } from "entities/RevenueSource";
import React, { useMemo } from "react";
import { useListContext, useTranslate } from "react-admin";

import { CustomerArticlesDatagrid } from "./CustomerArticlesDataGrid";
import { LaborCostDatagrid } from "./LaborCostDataGrid";
import { OPATChart } from "./OPATChart";
import { OrdersDatagrid } from "./OrdersDataGrid";
import { RevenueAndLaborCostChart } from "./RevenueAndLaborCostChart";

Chart.register(CategoryScale);

const prepareRevenueAndLaborCostChartData = (
	data,
	averageLaborPercentage,
	revenueTooltip,
	laborCostTooltip,
	laborPercentageTooltip,
	averageLaborPercentageTooltip
) => {
	const values = Object.values(data);

	return {
		labels: values.map((row) => row.grouping_period),
		datasets: [
			{
				type: "bar",
				label: revenueTooltip,
				data: values.map((row) => row.revenue),
				backgroundColor: ["rgba(75, 192, 192, 0.2)"],
				borderColor: ["rgba(75, 192, 192, 1)"],
				yAxisID: "y",
				borderWidth: 1,
			},
			{
				type: "bar",
				label: laborCostTooltip,
				data: values.map((row) => row.labor_cost),
				backgroundColor: ["rgba(255, 99, 132, 0.2)"],
				borderColor: ["rgba(255, 99, 132, 1)"],
				yAxisID: "y",
				borderWidth: 1,
			},
			{
				type: "line",
				label: laborPercentageTooltip,
				data: values.map((row) => row.labor_percentage),
				borderColor: ["rgba(255, 204, 153, 1)"],
				stack: "combined",
				yAxisID: "y2",
				borderWidth: 5,
			},
			{
				type: "line",
				label: averageLaborPercentageTooltip,
				data: values.map(() => averageLaborPercentage),
				borderColor: "rgba(255, 0, 0, 1)",
				yAxisID: "y2",
				borderWidth: 2,
				pointRadius: 0,
				pointHitRadius: 20,
			},
		],
	};
};

const prepareOpatChartData = (data, opatTooltip, manHoursTooltip) => {
	const values = Object.values(data);

	return {
		labels: values.map((row) => row.grouping_period),
		datasets: [
			{
				type: "bar",
				label: manHoursTooltip,
				data: values.map((row) => row.man_hours),
				backgroundColor: ["rgba(0, 128, 255, 0.2)"],
				borderColor: ["rgba(0, 128, 255, 1)"],
				stack: "combined",
				yAxisID: "y",
				borderWidth: 1,
			},
			{
				type: "line",
				label: opatTooltip,
				data: values.map((row) => row.opat),
				borderColor: ["rgba(255, 178, 102, 1)"],
				stack: "combined",
				yAxisID: "y2",
				borderWidth: 5,
			},
		],
	};
};

export const FinancialCharts = (props) => {
	const translate = useTranslate();
	const { filterValues } = useListContext();

	const {
		revenueTooltip,
		laborCostTooltip,
		laborPercentageTooltip,
		averageLaborPercentageTooltip,
		opatTooltip,
		manHoursTooltip,
	} = useMemo(
		() => ({
			revenueTooltip: translate("resources.financial-statements.barchart.revenue.label"),
			laborCostTooltip: translate("resources.financial-statements.barchart.labor-cost.label"),
			laborPercentageTooltip: translate("resources.financial-statements.barchart.labor-percentage.label"),
			averageLaborPercentageTooltip: translate(
				"resources.financial-statements.barchart.average-labor-percentage.label"
			),
			opatTooltip: translate("resources.financial-statements.barchart.opat.label"),
			manHoursTooltip: translate("resources.financial-statements.barchart.man-hours.label"),
		}),
		[translate]
	);

	const revenueAndLaborCostData = useMemo(() => {
		const hasData = props && props.data && Object.keys(props.data).length !== 0;
		return prepareRevenueAndLaborCostChartData(
			hasData ? props.data[1]["financial_statement_data"] : {},
			hasData ? props.data[1]["average_labor_percentage"] : 0,
			revenueTooltip,
			laborCostTooltip,
			laborPercentageTooltip,
			averageLaborPercentageTooltip
		);
	}, [props.data, revenueTooltip, laborCostTooltip, laborPercentageTooltip, averageLaborPercentageTooltip]);

	const opatData = useMemo(() => {
		return prepareOpatChartData(
			props && props.data && Object.keys(props.data).length !== 0
				? props.data[1]["opat_chart_data"]
				: {},
			opatTooltip,
			manHoursTooltip
		);
	}, [props.data, opatTooltip, manHoursTooltip]);

	if (Object.keys(props.data).length === 0) {
		return null;
	}

	const orders = props.data[1]["orders"];
	const laborCost = props.data[1]["labor_cost"];
	const customerArticles = props.data[1]["customer_articles"] ?? [];

	const isPeriodOneDay =
		filterValues["date_from"] &&
		filterValues["date_to"] &&
		filterValues["date_from"] === filterValues["date_to"];

	const revenueSource = filterValues.revenue_source ?? RevenueSource.ALL;
	const showOrders = revenueSource === RevenueSource.ORDERS || revenueSource === RevenueSource.ALL;
	const showWeeklyStorage =
		revenueSource === RevenueSource.WEEKLY_STORAGE || revenueSource === RevenueSource.ALL;

	return (
		<>
			<RevenueAndLaborCostChart chartData={revenueAndLaborCostData} />
			{isPeriodOneDay && (
				<>
					{showOrders && (
						<>
							<div align="center">
								<h1>{translate("resources.orders.name")}</h1>
							</div>
							<OrdersDatagrid orders={orders} />
						</>
					)}
					{showWeeklyStorage && (
						<>
							<div align="center">
								<h1>{translate("resources.financial-statements.barchart.weekly-storage.label")}</h1>
							</div>
							<CustomerArticlesDatagrid customerArticles={customerArticles} />
						</>
					)}
					<div align="center">
						<h1>{translate("resources.financial-statements.barchart.labor-cost.label")}</h1>
					</div>
					<LaborCostDatagrid laborCost={laborCost} />
				</>
			)}
			<OPATChart chartData={opatData} />
		</>
	);
};
